import fetcher from 'api/fetcher';
import { fetchCampaignInfo } from 'utils/campaign/fetchCampaignInfo';
import { MagritteNotificationName } from 'utils/constants/notification';

import urlParser from 'bloko/common/urlParser';

import { setCampaignsOrRefetchEmptyPage } from 'components/CampaignsDashboard/utils/setCampaignsOrRefetchEmptyPage';

import {
    CampaignArchiveReason,
    CampaignFrontendStatus,
    CampaignsTab,
    DashboardCampaign,
    deleteIdFromCheckedCampaignIds,
} from 'store/models/campaignsDashboard';
import { addMagritteNotification } from 'store/models/notification';
import { SessionType } from 'store/models/user';
import { AppDispatch, AppThunkAction } from 'store/utils';

const handleChangeStatusSuccess =
    (params: {
        campaignId: number;
        campaigns: Map<number, DashboardCampaign>;
        isCampaignDeletedFromPage: boolean;
        notificationIfDeleteFromPage: MagritteNotificationName;
        notificationIfLeftOnPage?: MagritteNotificationName;
    }) =>
    (dispatch: AppDispatch) => {
        const {
            campaignId,
            campaigns,
            isCampaignDeletedFromPage,
            notificationIfDeleteFromPage,
            notificationIfLeftOnPage,
        } = params;

        if (isCampaignDeletedFromPage) {
            dispatch(addMagritteNotification(notificationIfDeleteFromPage));
            dispatch(deleteIdFromCheckedCampaignIds(campaignId));
            campaigns.delete(campaignId);
            return;
        }

        if (notificationIfLeftOnPage) {
            dispatch(addMagritteNotification(notificationIfLeftOnPage));
        }
    };

export const updateCampaignStatus = (
    campaignId: number,
    newStatus: CampaignFrontendStatus.Blocked | CampaignFrontendStatus.Active | CampaignFrontendStatus.Archived,
    abortSignal: AbortSignal
): AppThunkAction<Promise<void | false>> => {
    return async (dispatch, getState) => {
        const campaigns = getState().campaignsDashboard.campaigns;
        const campaignsCurrentTab = getState().campaignsDashboard.campaignsCurrentTab;
        const isSudo = getState().user.sessionType === SessionType.Sudo;

        const queryStr = urlParser.stringify({
            newStatus,
            changeSource: 'CLICKME_CAMPAIGN_STATUS_TOGGLE',
        });

        const FETCH_PARAMS = {
            signal: abortSignal,
            timeout: 30000,
        };

        const fetchStatusChange =
            isSudo && newStatus === CampaignFrontendStatus.Archived
                ? fetcher.put(
                      `/api/v1/backoffice/campaign/${campaignId}/archive?archiveReason=${CampaignArchiveReason.WrongAccount}`,
                      undefined,
                      FETCH_PARAMS
                  )
                : fetcher.put(`/api/v1/campaigns/${campaignId}/status?${queryStr}`, null, FETCH_PARAMS);

        return fetchStatusChange
            .then(async () => {
                const newCampaignsList = new Map(campaigns);
                const currentCampaign = newCampaignsList.get(campaignId);

                if (!currentCampaign) {
                    return;
                }

                if ([CampaignFrontendStatus.Blocked, CampaignFrontendStatus.Archived].includes(newStatus)) {
                    newCampaignsList.set(campaignId, { ...currentCampaign, frontendStatus: newStatus });

                    if (newStatus === CampaignFrontendStatus.Blocked) {
                        dispatch(
                            handleChangeStatusSuccess({
                                campaignId,
                                campaigns: newCampaignsList,
                                isCampaignDeletedFromPage: [CampaignsTab.Active, CampaignsTab.Attention].includes(
                                    campaignsCurrentTab
                                ),
                                notificationIfDeleteFromPage:
                                    MagritteNotificationName.CampaignChangeStatusToPausedAndMoveSuccess,
                                notificationIfLeftOnPage: MagritteNotificationName.CampaignChangeStatusToPausedSuccess,
                            })
                        );
                    }

                    if (newStatus === CampaignFrontendStatus.Archived) {
                        dispatch(
                            handleChangeStatusSuccess({
                                campaignId,
                                campaigns: newCampaignsList,
                                isCampaignDeletedFromPage: [
                                    CampaignsTab.Active,
                                    CampaignsTab.Attention,
                                    CampaignsTab.Paused,
                                ].includes(campaignsCurrentTab),
                                notificationIfDeleteFromPage:
                                    MagritteNotificationName.CampaignChangeStatusToArchivedAndMoveSuccess,
                                notificationIfLeftOnPage:
                                    MagritteNotificationName.CampaignChangeStatusToArchivedSuccess,
                            })
                        );
                    }
                } else {
                    let newFrontendStatus: CampaignFrontendStatus = CampaignFrontendStatus.Unknown;
                    try {
                        const campaign = await fetchCampaignInfo(campaignId, abortSignal);
                        newFrontendStatus = campaign.frontendStatus;
                    } catch {
                        // newFrontendStatus остаётся Unknown
                    }

                    newCampaignsList.set(campaignId, {
                        ...currentCampaign,
                        frontendStatus: newFrontendStatus,
                    });

                    dispatch(
                        handleChangeStatusSuccess({
                            campaignId,
                            campaigns: newCampaignsList,
                            isCampaignDeletedFromPage:
                                campaignsCurrentTab === CampaignsTab.Paused &&
                                newFrontendStatus !== CampaignFrontendStatus.InactiveDueToEndDate,
                            notificationIfDeleteFromPage:
                                MagritteNotificationName.CampaignChangeStatusToActiveAndMoveSuccess,
                            notificationIfLeftOnPage: MagritteNotificationName.CampaignChangeStatusToActiveSuccess,
                        })
                    );
                }

                dispatch(setCampaignsOrRefetchEmptyPage(newCampaignsList, abortSignal));
            })
            .catch(() => {
                if (!abortSignal.aborted) {
                    dispatch(addMagritteNotification(MagritteNotificationName.CampaignChangeStatusFailed));
                }
            });
    };
};
